// Global Sensor Registry classes (issue #12) — module cache mirroring
// labelResolver's pattern. The api serves field_name → class/direction;
// the dashboard consults classOf()/isGraphable() to keep the behavior
// matrix honest client-side:
//   sensor → graphed/sparked; everything else → value-only display;
//   unknown fields QUARANTINE (treated like informational) until an admin
//   classifies them in the admin portal.

export type SensorClass = 'sensor' | 'state' | 'mode' | 'setting' | 'informational' | 'disregard';

interface ClassEntry { class: SensorClass; direction: string }

let cache = new Map<string, ClassEntry>();

export function setSensorClasses(entries: { field_name: string; class: SensorClass; direction: string }[]): void {
  const next = new Map<string, ClassEntry>();
  for (const e of entries) {
    if (e.field_name) next.set(e.field_name, { class: e.class, direction: e.direction });
  }
  cache = next;
}

export function clearSensorClasses(): void {
  cache = new Map();
}

/** Class for a field; unknown → 'informational' (quarantine semantics).
 *  Before the registry loads, fall back to 'sensor' so an empty cache
 *  (offline, first paint) never blanks every chart on the dashboard. */
export function classOf(field: string): SensorClass {
  if (cache.size === 0) return 'sensor';
  return cache.get(field)?.class ?? 'informational';
}

/** Only class=sensor is chartable telemetry. */
export function isGraphable(field: string): boolean {
  return classOf(field) === 'sensor';
}

/** disregard is stripped at NATS ingest (ingestQueue.ts) before it ever
 *  reaches the store, so this rarely matters — kept as defense in depth
 *  for any value that slips in another way (e.g. IDB replay of old data
 *  written before the field was reclassified). */
export function isDisregarded(field: string): boolean {
  return classOf(field) === 'disregard';
}

/** Everything except informational/disregard may participate in alert rules. */
export function isAlertable(field: string): boolean {
  const c = classOf(field);
  return c !== 'informational' && c !== 'disregard';
}
